import { useEffect, useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Plus,
  Trash2,
  Search,
  FolderKanban,
  User,
  Shirt,
  Layers,
  Sparkles,
  Edit3,
} from "lucide-react";
import PageHeader, { Page } from "@/components/layout/PageHeader";
import WorkspaceBanners from "@/components/common/WorkspaceBanners";
import { DemoBadge, EmptyState } from "@/components/common/States";
import MediaFrame from "@/components/common/MediaFrame";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import PendingDialog from "@/components/common/PendingDialog";
import CharacterModal from "@/components/character/CharacterModal";
import { useStudio, useLookups } from "@/store/StudioContext";
import { CHARACTER_KINDS } from "@/domain/entities";
import { getProjectCharacters } from "@/domain/librarySelectors";
import { t } from "@/i18n";

export const CharactersPage = ({ embeddedProjectId = null }) => {
  const { state, dispatch } = useStudio();
  const { assetById, projectById } = useLookups();
  const location = useLocation();
  const navigate = useNavigate();

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const queryProjectId = searchParams.get("projectId");
  const activeProjectId = embeddedProjectId || queryProjectId || null;

  const [filterKind, setFilterKind] = useState("all");
  const [filterProject, setFilterProject] = useState(activeProjectId || "all");
  const [searchQuery, setSearchQuery] = useState("");
  const [editingCharacter, setEditingCharacter] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [pending, setPending] = useState(false);

  // Sincronizar si cambia el proyecto activo embebido
  useEffect(() => {
    if (activeProjectId) {
      setFilterProject(activeProjectId);
    }
  }, [activeProjectId]);

  // Determinar la lista de personajes según los filtros
  const characterList = useMemo(() => {
    let list = state.characters || [];

    // Filtro por proyecto
    if (filterProject === "no_project") {
      list = list.filter((c) => !c.projectId);
    } else if (filterProject !== "all") {
      list = getProjectCharacters(state, filterProject);
    }

    // Filtro por categoría/kind
    if (filterKind !== "all") {
      list = list.filter((c) => c.kind === filterKind);
    }

    // Búsqueda por texto (nombre o @handle)
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      list = list.filter(
        (c) =>
          (c.name || "").toLowerCase().includes(q) ||
          (c.handle || "").toLowerCase().includes(q) ||
          (c.description || "").toLowerCase().includes(q)
      );
    }

    return list;
  }, [state, filterProject, filterKind, searchQuery]);

  // Contar escenas en las que se usa el personaje
  const getUsedInScenesCount = (c) => {
    const refIds = (state.references || [])
      .filter((r) => r.characterId === c.id)
      .map((r) => r.id);
    return (state.scenes || []).filter(
      (s) => !s.isDemo && (s.referenceIds || []).some((rId) => refIds.includes(rId))
    ).length;
  };

  const projectResourcesCount = useMemo(() => {
    return activeProjectId ? getProjectCharacters(state, activeProjectId).length : 0;
  }, [state, activeProjectId]);

  const totalResourcesCount = useMemo(() => {
    return (state.characters || []).length;
  }, [state.characters]);

  // Prefill desde Galería → "Usar como referencia": abre el modal con la imagen preasignada
  useEffect(() => {
    if (location.state?.prefillMasterAssetId) {
      const assetId = location.state.prefillMasterAssetId;
      setEditingCharacter({
        id: null,
        name: "",
        handle: "",
        kind: "character",
        description: "",
        outfit: "",
        masterAssetId: assetId,
        views: [
          {
            id: `vw_prefill_${Date.now()}`,
            assetId,
            viewType: "front",
            isMaster: true,
            label: "Frontal",
          },
        ],
        prefilled: true,
      });
      setIsModalOpen(true);
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.state, location.pathname, navigate]);

  const handleOpenCreate = () => {
    setEditingCharacter(null);
    setIsModalOpen(true);
  };

  const handleOpenEdit = (character) => {
    setEditingCharacter(character);
    setIsModalOpen(true);
  };

  const handleSaveCharacter = (data) => {
    if (data.id) {
      dispatch({
        type: "CHARACTER_UPDATE",
        payload: {
          id: data.id,
          patch: {
            name: data.name,
            handle: data.handle,
            kind: data.kind,
            description: data.description,
            outfit: data.outfit,
            baseWardrobe: data.baseWardrobe,
            projectId: data.projectId,
            scope: data.scope || (data.projectId ? "project" : "pending_review"),
            reviewStatus: data.projectId ? "confirmed" : (data.scope === "global" ? "confirmed" : "pending_review"),
            views: data.views,
            masterAssetId: data.masterAssetId,
            viewAssetIds: data.viewAssetIds,
          },
        },
      });
    } else {
      dispatch({
        type: "CHARACTER_CREATE",
        payload: data,
      });
    }
    setEditingCharacter(null);
  };

  const handleDeleteCharacter = () => {
    if (!toDelete?.id) return;
    dispatch({ type: "CHARACTER_DELETE", payload: { id: toDelete.id } });
    setToDelete(null);
  };

  const content = (
    <>
      {embeddedProjectId ? (
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#262626] pb-4 mb-4">
          <div>
            <h1 className="font-display text-lg font-semibold text-[#f4f1ec]">
              {filterProject === activeProjectId
                ? (t.project.projectResources || "Recursos del proyecto")
                : "Todos mis recursos"}
            </h1>
            <p className="text-xs text-[#a3a3a3]">
              {filterProject === activeProjectId
                ? "Personajes, objetos y referencias de este proyecto."
                : "Vista general de consulta de todos los recursos del estudio."}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {activeProjectId && (
              <div className="flex items-center gap-1 rounded-lg border border-[#303030] bg-[#101010] p-0.5">
                <button
                  type="button"
                  data-testid="characters-tab-project"
                  onClick={() => setFilterProject(activeProjectId)}
                  className={`flex items-center gap-1.5 rounded-md px-3 py-1 text-xs font-medium transition-colors ${
                    filterProject === activeProjectId
                      ? "bg-[#f5b544] text-[#1a1206]"
                      : "text-[#a3a3a3] hover:text-[#f4f1ec]"
                  }`}
                >
                  <span>{t.project.projectResources || "Recursos del proyecto"}</span>
                  <span className={`rounded-full px-1.5 py-0.2 text-[10px] ${filterProject === activeProjectId ? "bg-[#1a1206]/20 font-bold" : "bg-[#222] text-[#888]"}`}>
                    {projectResourcesCount}
                  </span>
                </button>
                <button
                  type="button"
                  data-testid="characters-tab-all"
                  onClick={() => setFilterProject("all")}
                  className={`flex items-center gap-1.5 rounded-md px-3 py-1 text-xs font-medium transition-colors ${
                    filterProject === "all"
                      ? "bg-[#f5b544] text-[#1a1206]"
                      : "text-[#a3a3a3] hover:text-[#f4f1ec]"
                  }`}
                >
                  <span>Todos mis recursos</span>
                  <span className={`rounded-full px-1.5 py-0.2 text-[10px] ${filterProject === "all" ? "bg-[#1a1206]/20 font-bold" : "bg-[#222] text-[#888]"}`}>
                    {totalResourcesCount}
                  </span>
                </button>
              </div>
            )}
            <button
              data-testid="characters-new"
              className="v-btn-primary flex items-center gap-1.5 text-xs py-1.5"
              onClick={handleOpenCreate}
            >
              <Plus size={15} />
              {t.characters.new}
            </button>
          </div>
        </div>
      ) : (
        <>
          <PageHeader
            title={t.characters.title}
            subtitle={t.characters.subtitle}
            right={
              <button
                data-testid="characters-new"
                className="v-btn-primary flex items-center gap-1.5"
                onClick={handleOpenCreate}
              >
                <Plus size={16} />
                {t.characters.new}
              </button>
            }
          />
          <WorkspaceBanners />
        </>
      )}

      {/* Barra de Filtros, Búsqueda y Selección de Proyecto */}
      <div className="mb-6 flex flex-col gap-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          {/* Selector de Categorías */}
          <div className="flex flex-wrap gap-1.5">
            {["all", ...CHARACTER_KINDS].map((k) => (
              <button
                key={k}
                data-testid={`characters-filter-${k}`}
                onClick={() => setFilterKind(k)}
                className={`v-btn-ghost v-btn-sm text-xs transition-colors ${
                  filterKind === k ? "border-[#f5b544] bg-[#f5b544]/10 text-[#f5b544]" : "text-[#a3a3a3]"
                }`}
              >
                {k === "all" ? t.common.all : t.characters.kinds[k]}
              </button>
            ))}
          </div>

          {/* Filtro por Proyecto y Barra de Búsqueda */}
          <div className="flex flex-wrap items-center gap-2">
            {!embeddedProjectId && activeProjectId && (
              <div className="flex items-center gap-1 rounded-lg border border-[#303030] bg-[#101010] p-0.5 mr-2">
                <button
                  type="button"
                  data-testid="characters-tab-project"
                  onClick={() => setFilterProject(activeProjectId)}
                  className={`flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                    filterProject === activeProjectId
                      ? "bg-[#f5b544] text-[#1a1206]"
                      : "text-[#a3a3a3] hover:text-[#f4f1ec]"
                  }`}
                >
                  <span>{t.project.projectResources || "Recursos del proyecto"}</span>
                </button>
                <button
                  type="button"
                  data-testid="characters-tab-all"
                  onClick={() => setFilterProject("all")}
                  className={`flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                    filterProject === "all"
                      ? "bg-[#f5b544] text-[#1a1206]"
                      : "text-[#a3a3a3] hover:text-[#f4f1ec]"
                  }`}
                >
                  <span>Todos mis recursos</span>
                </button>
              </div>
            )}

            {state.projects.length > 0 && (
              <div className="flex items-center gap-1 rounded-lg border border-[#303030] bg-[#161616] px-2.5 py-1 text-xs">
                <FolderKanban size={13} className="text-[#888]" />
                <select
                  data-testid="characters-project-filter"
                  value={filterProject}
                  onChange={(e) => setFilterProject(e.target.value)}
                  className="bg-transparent text-xs text-[#ccc] focus:outline-none"
                >
                  <option value="all" className="bg-[#1a1a1a]">
                    {t.characters.allProjects}
                  </option>
                  {state.projects.map((p) => (
                    <option key={p.id} value={p.id} className="bg-[#1a1a1a]">
                      {p.name || p.id}
                    </option>
                  ))}
                  <option value="no_project" className="bg-[#1a1a1a]">
                    Sin proyecto
                  </option>
                </select>
              </div>
            )}

            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#777]" />
              <input
                data-testid="characters-search"
                type="text"
                placeholder={t.characters.searchPlaceholder}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="v-input h-8 w-48 pl-8 text-xs sm:w-60"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Lista de Personajes en Cuadrícula */}
      {characterList.length === 0 ? (
        <EmptyState
          title={t.characters.empty}
          body={t.characters.emptyBody}
          testId="characters-empty"
        />
      ) : (
        <div
          data-testid="characters-grid"
          className="grid gap-4"
          style={{ gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))" }}
        >
          {characterList.map((c, i) => {
            const masterAsset = assetById[c.masterAssetId];
            const viewsCount = (c.views || c.viewAssetIds || []).length;
            const project = projectById[c.projectId];
            const usedCount = getUsedInScenesCount(c);

            return (
              <div
                key={c.id}
                data-testid={`character-card-${c.id}`}
                className="group flex flex-col overflow-hidden rounded-xl border border-[#2a2a2a] bg-[#161616] transition-all hover:border-[#444] hover:shadow-lg"
                style={{ animationDelay: `${i * 25}ms` }}
              >
                {/* Contenedor de la Imagen Principal */}
                <div
                  onClick={() => handleOpenEdit(c)}
                  className="relative aspect-[4/5] w-full cursor-pointer bg-[#121212] overflow-hidden"
                >
                  {masterAsset ? (
                    <MediaFrame
                      asset={masterAsset}
                      fit="contain"
                      className="h-full w-full bg-[#121212] transition-transform duration-300 group-hover:scale-105"
                      showBadge={false}
                    />
                  ) : (
                    <div className="flex h-full w-full flex-col items-center justify-center gap-1.5 text-[#555]">
                      <User size={38} className="opacity-30" />
                      <span className="text-[11px] text-[#777]">{t.characters.noMaster}</span>
                    </div>
                  )}

                  {/* Badge de Categoría */}
                  <div className="absolute left-2 top-2 rounded bg-black/60 px-2 py-0.5 text-[10px] font-medium text-[#ccc] backdrop-blur-md">
                    {t.characters.kinds[c.kind] || c.kind}
                  </div>

                  {c.isDemo && (
                    <div className="absolute right-2 top-2">
                      <DemoBadge />
                    </div>
                  )}

                  {/* Contador de Vistas */}
                  {viewsCount > 1 && (
                    <div className="absolute bottom-2 left-2 flex items-center gap-1 rounded bg-black/70 px-2 py-0.5 text-[10px] font-medium text-[#f4f1ec] backdrop-blur-md">
                      <Layers size={11} className="text-[#f5b544]" />
                      <span>{t.characters.viewsCount(viewsCount)}</span>
                    </div>
                  )}
                </div>

                {/* Contenido / Metadatos de la tarjeta */}
                <div className="flex flex-1 flex-col p-3">
                  <div className="min-w-0">
                    <h3
                      onClick={() => handleOpenEdit(c)}
                      className="cursor-pointer truncate font-display text-sm font-semibold text-[#f4f1ec] transition-colors hover:text-[#f5b544]"
                      title={c.name}
                    >
                      {c.name || "Sin nombre"}
                    </h3>
                    <div className="truncate font-mono text-xs text-[#f5b544]">
                      @{c.handle}
                    </div>
                  </div>

                  {/* Descripción o Vestuario Base si existe */}
                  {c.outfit ? (
                    <div
                      className="mt-2 flex items-start gap-1 text-[11px] text-[#999]"
                      title={`Vestuario base: ${c.outfit}`}
                    >
                      <Shirt size={12} className="mt-0.5 shrink-0 text-[#888]" />
                      <span className="line-clamp-1">{c.outfit}</span>
                    </div>
                  ) : c.description ? (
                    <p className="mt-2 line-clamp-1 text-[11px] text-[#888]" title={c.description}>
                      {c.description}
                    </p>
                  ) : null}

                  {/* Pie de tarjeta: Proyecto y Escenas */}
                  <div className="mt-auto pt-3">
                    <div className="flex items-center justify-between text-[11px] text-[#777]">
                      {filterProject === "all" ? (
                        project ? (
                          <span className="truncate max-w-[100px]" title={project.name}>
                            {project.name}
                          </span>
                        ) : (
                          <span
                            data-testid={`character-no-project-${c.id}`}
                            className="rounded bg-[#222] px-1.5 py-0.5 text-[10px] text-[#888] italic"
                          >
                            Sin proyecto
                          </span>
                        )
                      ) : filterProject === "no_project" ? (
                        <span
                          data-testid={`character-no-project-${c.id}`}
                          className="rounded bg-[#222] px-1.5 py-0.5 text-[10px] text-[#888] italic"
                        >
                          Sin proyecto
                        </span>
                      ) : (
                        <span>{t.characters.views}: {viewsCount}</span>
                      )}
                      <span>{t.characters.usedIn(usedCount)}</span>
                    </div>

                    {/* Botones de acción */}
                    <div className="mt-2.5 flex items-center gap-1 border-t border-[#242424] pt-2">
                      <button
                        data-testid={`character-edit-${c.id}`}
                        onClick={() => handleOpenEdit(c)}
                        className="v-btn-ghost v-btn-sm h-7 flex-1 px-2 text-xs"
                      >
                        <Edit3 size={12} />
                        {t.common.edit}
                      </button>
                      <button
                        data-testid={`character-request-view-${c.id}`}
                        onClick={() => setPending(true)}
                        className="v-btn-ghost v-btn-sm h-7 px-2 text-xs text-[#888] hover:text-[#ccc]"
                        title={t.characters.requestView}
                      >
                        <Sparkles size={12} />
                      </button>
                      <button
                        data-testid={`character-delete-${c.id}`}
                        onClick={() => setToDelete(c)}
                        className="rounded-md p-1.5 text-[#666] transition-colors hover:bg-rose-950/40 hover:text-rose-400"
                        aria-label={t.common.delete}
                        title={t.common.delete}
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal de Crear / Editar Personaje */}
      <CharacterModal
        key={
          editingCharacter?.id
            ? `edit_${editingCharacter.id}`
            : editingCharacter?.prefilled
            ? `prefill_${editingCharacter.masterAssetId}`
            : "create"
        }
        open={isModalOpen}
        onOpenChange={(isOpen) => {
          setIsModalOpen(isOpen);
          if (!isOpen) {
            setEditingCharacter(null);
          }
        }}
        character={editingCharacter}
        defaultProjectId={activeProjectId || (filterProject !== "all" && filterProject !== "no_project" ? filterProject : state.projects[0]?.id || null)}
        onSave={handleSaveCharacter}
      />

      {/* Diálogo de Confirmación de Borrado */}
      <ConfirmDialog
        open={Boolean(toDelete)}
        onOpenChange={(o) => !o && setToDelete(null)}
        title={toDelete?.name || ""}
        body={t.characters.deleteConfirm}
        onConfirm={handleDeleteCharacter}
      />

      {/* Diálogo para funciones pendientes */}
      <PendingDialog open={pending} onOpenChange={setPending} registered={false} />
    </>
  );

  return embeddedProjectId ? (
    <div data-testid="characters-page" className="p-4 md:p-6 flex-1 min-h-0">
      {content}
    </div>
  ) : (
    <Page testId="characters-page">
      {content}
    </Page>
  );
};

export default CharactersPage;
